import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileSpreadsheet, Search, Upload, Archive, ArchiveRestore, ListChecks, FileStack, AlertTriangle, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { ActionsLibraryTab } from '../components/reviews/ActionsLibraryTab';

const PERIOD_TYPES = [
  { v: 'month',   label: 'Month' },
  { v: 'quarter', label: 'Quarter' },
  { v: 'fy',      label: 'FY' },
  { v: 'custom',  label: 'Custom' },
  { v: 'other',   label: 'Other' },
];

function NewReviewModal({ open, onClose, outlets, portfolios, onCreated, viewerMode }) {
  const { API, getHeaders, user } = useAuth();
  const [scopeType, setScopeType] = useState('outlet');
  const [outletId, setOutletId] = useState('');
  const [portfolioId, setPortfolioId] = useState('');
  const [title, setTitle] = useState('');
  const [periodType, setPeriodType] = useState('month');
  const [periodLabel, setPeriodLabel] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [series, setSeries] = useState([]);
  const [seriesId, setSeriesId] = useState('__none__');
  const [newSeriesName, setNewSeriesName] = useState('');

  useEffect(() => {
    if (open) {
      setScopeType('outlet');
      const defaultOutlet = viewerMode === 'manager'
        ? (user?.assigned_outlets?.[0]?.id || '')
        : (outlets?.[0]?.id || '');
      setOutletId(defaultOutlet);
      setPortfolioId(portfolios?.[0]?.id || '');
      setTitle(''); setPeriodLabel(''); setPeriodType('month'); setFile(null);
      setSeriesId('__none__'); setNewSeriesName('');
    }
  }, [open, outlets, portfolios, user, viewerMode]);

  // Load series scoped to current selection
  useEffect(() => {
    if (!open) return;
    const scopeIdForSeries = scopeType === 'outlet' ? outletId : portfolioId;
    if (!scopeIdForSeries) { setSeries([]); return; }
    (async () => {
      try {
        const p = new URLSearchParams({ scope_type: scopeType, scope_id: scopeIdForSeries });
        const r = await fetch(`${API}/review-series?${p.toString()}`, { headers: getHeaders() });
        if (r.ok) setSeries((await r.json()).items || []);
      } catch { /* silent */ }
    })();
  }, [API, getHeaders, open, scopeType, outletId, portfolioId]);

  const submit = async () => {
    if (scopeType === 'outlet' && !outletId) return toast.error('Select an outlet');
    if (scopeType === 'portfolio' && !portfolioId) return toast.error('Select a portfolio');
    if (!title.trim()) return toast.error('Enter a review title');
    if (!file) return toast.error('Choose an .xlsx file');
    if (seriesId === '__new__' && !newSeriesName.trim())
      return toast.error('Enter new series name');
    const fd = new FormData();
    fd.append('scope_type', scopeType);
    if (scopeType === 'outlet') fd.append('outlet_id', outletId);
    else fd.append('portfolio_id', portfolioId);
    fd.append('title', title.trim());
    fd.append('period_type', periodType);
    fd.append('period_label', periodLabel.trim());
    fd.append('file', file);
    setBusy(true);
    try {
      const headers = getHeaders();
      delete headers['Content-Type'];
      const r = await fetch(`${API}/reviews`, {
        method: 'POST', headers, body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Upload failed');
      }
      const created = await r.json();
      // Link series if selected/new
      let targetSeriesId = null;
      if (seriesId === '__new__') {
        try {
          const rs = await fetch(`${API}/review-series`, {
            method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newSeriesName.trim(), scope_type: scopeType,
                                    scope_id: scopeType === 'outlet' ? outletId : portfolioId }),
          });
          if (rs.ok) targetSeriesId = (await rs.json()).id;
        } catch { /* non-fatal */ }
      } else if (seriesId !== '__none__') {
        targetSeriesId = seriesId;
      }
      if (targetSeriesId) {
        try {
          await fetch(`${API}/reviews/${created.id}/series`, {
            method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ review_series_id: targetSeriesId }),
          });
        } catch { /* non-fatal */ }
      }
      toast.success(`Review uploaded · ${created.sheet_count} sheet(s)`);
      onCreated?.(created);
      onClose();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setBusy(false); }
  };

  const availableOutlets = viewerMode === 'manager'
    ? (user?.assigned_outlets || [])
    : outlets;
  const canPickPortfolio = viewerMode !== 'manager';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="new-review-modal">
        <DialogHeader>
          <DialogTitle className="text-[color:var(--color-primary-hover)]">New Review</DialogTitle>
          <DialogDescription className="text-xs text-[#5C6B64]">
            Upload an Excel workbook (.xlsx or .xlsm, max 25 MB). Source file is preserved unchanged; annotations are stored separately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {canPickPortfolio && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#7A8B82]">Scope</Label>
              <div className="mt-1 inline-flex rounded-md border border-[#E7EBE6] overflow-hidden" data-testid="nr-scope">
                <button type="button"
                        onClick={() => setScopeType('outlet')}
                        data-testid="nr-scope-outlet"
                        className={`px-3 py-1.5 text-xs font-medium ${scopeType === 'outlet'
                          ? 'bg-[color:var(--color-primary)] text-white'
                          : 'bg-white text-[#5C6B64] hover:bg-[#F3F5F2]'}`}>
                  Outlet
                </button>
                <button type="button"
                        onClick={() => setScopeType('portfolio')}
                        data-testid="nr-scope-portfolio"
                        className={`px-3 py-1.5 text-xs font-medium border-l border-[#E7EBE6] ${scopeType === 'portfolio'
                          ? 'bg-[color:var(--color-primary)] text-white'
                          : 'bg-white text-[#5C6B64] hover:bg-[#F3F5F2]'}`}>
                  Portfolio
                </button>
              </div>
            </div>
          )}
          {scopeType === 'outlet' ? (
            <div>
              <Label htmlFor="nr-outlet" className="text-xs uppercase tracking-wider text-[#7A8B82]">Outlet</Label>
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger id="nr-outlet" data-testid="nr-outlet" className="mt-1">
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {availableOutlets.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="nr-portfolio" className="text-xs uppercase tracking-wider text-[#7A8B82]">Portfolio</Label>
              <Select value={portfolioId} onValueChange={setPortfolioId}>
                <SelectTrigger id="nr-portfolio" data-testid="nr-portfolio" className="mt-1">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {(portfolios || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="nr-title" className="text-xs uppercase tracking-wider text-[#7A8B82]">Review Title</Label>
            <Input id="nr-title" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. August P&L Review"
                   data-testid="nr-title" className="mt-1"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nr-ptype" className="text-xs uppercase tracking-wider text-[#7A8B82]">Period Type</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger id="nr-ptype" data-testid="nr-period-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="nr-plabel" className="text-xs uppercase tracking-wider text-[#7A8B82]">Period Label</Label>
              <Input id="nr-plabel" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)}
                     placeholder="e.g. Aug 2026 or Apr–Jun 2026"
                     data-testid="nr-period-label" className="mt-1"/>
            </div>
          </div>
          <div>
            <Label htmlFor="nr-series" className="text-xs uppercase tracking-wider text-[#7A8B82]">Review Series (optional)</Label>
            <Select value={seriesId} onValueChange={setSeriesId}>
              <SelectTrigger id="nr-series" data-testid="nr-series" className="mt-1">
                <SelectValue placeholder="One-off (no series)"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">One-off / No Series</SelectItem>
                {series.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`nr-series-opt-${s.id}`}>{s.name}</SelectItem>
                ))}
                <SelectItem value="__new__" data-testid="nr-series-new-opt">+ New Review Series…</SelectItem>
              </SelectContent>
            </Select>
            {seriesId === '__new__' && (
              <Input value={newSeriesName} onChange={(e) => setNewSeriesName(e.target.value)}
                     placeholder="e.g. Monthly P&L Review — Woodland"
                     data-testid="nr-series-new-name"
                     className="mt-2"/>
            )}
            <p className="text-[10px] text-[#7A8B82] mt-1">Reviews in the same series show carry-forward actions from the prior meeting.</p>
          </div>
          <div>
            <Label htmlFor="nr-file" className="text-xs uppercase tracking-wider text-[#7A8B82]">Excel file (.xlsx)</Label>
            <Input id="nr-file" type="file" accept=".xlsx,.xlsm"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="nr-file" className="mt-1"/>
            <p className="text-[10px] text-[#7A8B82] mt-1">Max 25 MB. Source file is stored immutably.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="nr-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="nr-submit"
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white">
            {busy ? 'Uploading…' : 'Upload Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReviewLibrary({ viewerMode = 'admin' } = {}) {
  const { API, getHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [status, setStatus] = useState('active');
  const [outletFilter, setOutletFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [periodTypeFilter, setPeriodTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isManager = viewerMode === 'manager';
  const [subTab, setSubTab] = useState('reviews');

  useEffect(() => {
    (async () => {
      if (isManager) {
        setOutlets(user?.assigned_outlets || []);
      } else {
        try {
          const [rO, rP] = await Promise.all([
            fetch(`${API}/outlets`, { headers: getHeaders() }),
            fetch(`${API}/admin/portfolios`, { headers: getHeaders() }),
          ]);
          if (rO.ok) setOutlets(await rO.json());
          if (rP.ok) setPortfolios(await rP.json());
        } catch { /* silent */ }
      }
    })();
  }, [API, getHeaders, isManager, user]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (scopeFilter) params.set('scope_type', scopeFilter);
      if (outletFilter) params.set('outlet_ids', outletFilter);
      if (periodTypeFilter) params.set('period_type', periodTypeFilter);
      if (search.trim()) params.set('search', search.trim());
      const r = await fetch(`${API}/reviews?${params.toString()}`, { headers: getHeaders() });
      if (!r.ok) throw new Error('Fetch failed');
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, outletFilter, scopeFilter, periodTypeFilter, search]);

  const openReview = (id) => {
    navigate((isManager ? '/manager' : '/admin') + `/reviews/${id}`);
  };

  const toggleArchive = async (id, cur) => {
    try {
      const r = await fetch(`${API}/reviews/${id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: cur === 'archived' ? 'active' : 'archived' }),
      });
      if (!r.ok) throw new Error('Failed');
      toast.success(cur === 'archived' ? 'Restored' : 'Archived');
      load();
    } catch (e) { toast.error(String(e.message || e)); }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6" data-testid="review-library">
      <header className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[color:var(--color-text-primary)]">Review &amp; Discussion</h1>
          <p className="text-sm text-[#5C6B64] mt-1">Upload Excel review workbooks — annotate, present, retain history.</p>
        </div>
        {subTab === 'reviews' && (
          <Button onClick={() => setModalOpen(true)}
                  data-testid="new-review-btn"
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1.5">
            <Upload className="w-4 h-4" /> New Review
          </Button>
        )}
      </header>

      {/* Reviews | Actions sub-tabs */}
      <div className="flex gap-1 border-b border-[#E7EBE6] mb-4" data-testid="rl-subtabs">
        <button onClick={() => setSubTab('reviews')} data-testid="rl-tab-reviews"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                  subTab === 'reviews'
                    ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary-hover)]'
                    : 'border-transparent text-[#5C6B64] hover:text-[#1A2421]'}`}>
          <FileStack className="w-4 h-4"/> Reviews
        </button>
        <button onClick={() => setSubTab('actions')} data-testid="rl-tab-actions"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                  subTab === 'actions'
                    ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary-hover)]'
                    : 'border-transparent text-[#5C6B64] hover:text-[#1A2421]'}`}>
          <ListChecks className="w-4 h-4"/> Actions
        </button>
      </div>

      {subTab === 'actions' ? (
        <ActionsLibraryTab viewerMode={viewerMode}/>
      ) : (
        <>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-white border border-[#E7EBE6] rounded-md" data-testid="review-filters">
        {!isManager && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Scope</Label>
            <Select value={scopeFilter || 'all'} onValueChange={(v) => setScopeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-32 mt-1" data-testid="filter-scope"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="outlet">Outlet</SelectItem>
                <SelectItem value="portfolio">Portfolio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {!isManager && (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Outlet</Label>
            <Select value={outletFilter || 'all'} onValueChange={(v) => setOutletFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-52 mt-1" data-testid="filter-outlet"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outlets</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Period Type</Label>
          <Select value={periodTypeFilter || 'all'} onValueChange={(v) => setPeriodTypeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 mt-1" data-testid="filter-period-type"><SelectValue placeholder="Any"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              {PERIOD_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 mt-1" data-testid="filter-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#7A8B82]"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search title…" className="pl-8"
                   data-testid="filter-search"/>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#E7EBE6] rounded-md bg-white overflow-hidden" data-testid="review-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F5F2]">
            <tr className="text-[10px] uppercase tracking-widest text-[#7A8B82]">
              <th className="text-left px-3 py-2 font-semibold">Title</th>
              <th className="text-left px-3 py-2 font-semibold">Outlet</th>
              <th className="text-left px-3 py-2 font-semibold">Period</th>
              <th className="text-left px-3 py-2 font-semibold">File</th>
              <th className="text-left px-3 py-2 font-semibold">Uploaded By</th>
              <th className="text-left px-3 py-2 font-semibold">Created</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-right px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center px-3 py-8 text-[#7A8B82]">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center px-3 py-10 text-[#7A8B82]">
                  <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-40"/>
                  No reviews yet. Click <span className="font-semibold">New Review</span> to upload the first one.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[#E7EBE6] hover:bg-[#F9F9F8]" data-testid={`review-row-${it.id}`}>
                <td className="px-3 py-2">
                  <button onClick={() => openReview(it.id)}
                          className="text-[color:var(--color-primary-hover)] font-semibold hover:underline text-left"
                          data-testid={`open-review-${it.id}`}>
                    {it.title}
                  </button>
                </td>
                <td className="px-3 py-2 text-[#5C6B64]">
                  <span className="inline-block text-[10px] uppercase tracking-wider bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)] px-1.5 py-0.5 rounded mr-1.5">
                    {(it.scope_type || 'outlet')}
                  </span>
                  {it.scope_type === 'portfolio' ? (it.portfolio_name || it.portfolio_id || '—') : (it.outlet_name || it.outlet_id)}
                </td>
                <td className="px-3 py-2 text-[#5C6B64]">
                  <span className="inline-block text-[10px] uppercase tracking-wider bg-[#F3F5F2] px-1.5 py-0.5 rounded mr-1.5">{it.period_type}</span>
                  {it.period_label || '—'}
                </td>
                <td className="px-3 py-2 text-[#5C6B64] text-xs">{it.original_filename}</td>
                <td className="px-3 py-2 text-[#5C6B64] text-xs">{it.created_by_email || '—'}</td>
                <td className="px-3 py-2 text-[#5C6B64] text-xs">{fmt(it.created_at)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    it.status === 'archived' ? 'bg-[#F3F5F2] text-[#7A8B82]' : 'bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)]'}`}>
                    {it.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => toggleArchive(it.id, it.status)}
                          className="text-[#5C6B64] hover:text-[color:var(--color-primary)] p-1"
                          title={it.status === 'archived' ? 'Restore' : 'Archive'}
                          data-testid={`toggle-archive-${it.id}`}>
                    {it.status === 'archived' ? <ArchiveRestore className="w-4 h-4"/> : <Archive className="w-4 h-4"/>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewReviewModal open={modalOpen} onClose={() => setModalOpen(false)}
                       outlets={outlets} portfolios={portfolios}
                       onCreated={load} viewerMode={viewerMode}/>
        </>
      )}
    </div>
  );
}
